import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";
import type { Partner } from "./AddTransactionForm";

interface ExpenseRow {
  id: string;
  investor: string;
  email: string;
  amount: number | null;
  date: string;
  invoice_name: string;
  invoice_link: string;
  /** Where the file is in Drive, e.g. "My Drive/invoice/<file name>". */
  invoice_path: string;
  invoice_mime_type: string;
  has_invoice: boolean;
}

interface Draft {
  partner_email: string;
  amount: string;
  date: string;
}

const MAX_MB = 10;
const ACCEPT = "application/pdf,image/*";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function money(n: number | null) {
  if (n === null || n === undefined) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  if (e?.response?.status === 413) return `The invoice must be ${MAX_MB} MB or smaller.`;
  return "Request failed. Please try again.";
}

interface Props {
  /** Admins manage everyone's expenses; other users only see and change their own. */
  isAdmin?: boolean;
}

export default function ExpensesManager({ isAdmin = true }: Props) {
  const [rows, setRows] = useState<ExpenseRow[] | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editRow, setEditRow] = useState<ExpenseRow | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ row: ExpenseRow; url: string; type: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  // For a non-admin: their own partner record, null while loading, false when their login isn't a partner.
  const [me, setMe] = useState<Partner | null | false>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<ExpenseRow[]>("/expenses");
      setRows(data);
    } catch (e) {
      setRows([]);
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get<Partner[]>("/partners").then(({ data }) => setPartners(data)).catch(() => setPartners([]));
    } else {
      api.get<{ partner: Partner | null }>("/expenses/me")
        .then(({ data }) => { setMe(data.partner ?? false); setPartners(data.partner ? [data.partner] : []); })
        .catch(() => { setMe(false); setPartners([]); });
    }
  }, [load, isAdmin]);

  const openForm = (row?: ExpenseRow) => {
    setError(null);
    setNotice(null);
    setFile(null);
    setEditRow(row ?? null);
    setDraft(row
      ? { partner_email: row.email.toLowerCase(), amount: String(row.amount ?? ""), date: row.date }
      : { partner_email: !isAdmin && me ? me.id : "", amount: "", date: today() });
    if (row) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => { setDraft(null); setEditRow(null); setFile(null); setError(null); };

  const pickFile = (f: File | null) => {
    setError(null);
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setError(`The invoice must be ${MAX_MB} MB or smaller.`);
      if (fileInput.current) fileInput.current.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const save = async () => {
    if (!draft) return;
    const missing = [
      !draft.partner_email && "investor",
      !(Number(draft.amount) > 0) && "an expense amount above 0",
      !draft.date && "a date",
    ].filter(Boolean);
    if (missing.length) {
      setError(`Please choose ${missing.join(", ")}.`);
      return;
    }

    const body = new FormData();
    body.append("partner_email", draft.partner_email);
    body.append("amount", draft.amount);
    body.append("date", draft.date);
    if (file) body.append("invoice", file);

    setBusy(true);
    setError(null);
    try {
      if (editRow) {
        const { data } = await api.put<ExpenseRow>(`/expenses/${editRow.id}`, body);
        setNotice(`Updated ${data.investor}'s ${money(data.amount)} expense on ${data.date}.` + (file ? (editRow.has_invoice ? " The invoice was replaced." : " The invoice was saved to Google Drive.") : ""));
      } else {
        const { data } = await api.post<ExpenseRow>("/expenses", body);
        setNotice(`Added ${data.investor}'s ${money(data.amount)} expense.` +
          (data.has_invoice ? ` Invoice saved to Google Drive at "${data.invoice_path || data.invoice_name}".` : " You can attach an invoice later with Edit."));
      }
      closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: ExpenseRow) => {
    const ok = window.confirm(
      `Delete this expense?\n\n${row.investor} · ${row.date} · ${money(row.amount)}\n\n` +
      "Its invoice will be moved to the Google Drive trash.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/expenses/${row.id}`);
      setNotice(`Deleted ${row.investor}'s ${money(row.amount)} expense on ${row.date}.`);
      if (editRow?.id === row.id) closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // Shows the invoice in a preview on this page. It is fetched through the backend, so it works
  // whichever Google account the viewer is signed in to.
  const viewInvoice = async (row: ExpenseRow) => {
    setOpening(row.id);
    setError(null);
    try {
      const { data } = await api.get<Blob>(`/expenses/${row.id}/invoice`, { responseType: "blob" });
      const type = data.type || row.invoice_mime_type;
      setPreview({ row, url: URL.createObjectURL(type && data.type !== type ? new Blob([data], { type }) : data), type });
    } catch (e: any) {
      let message = "Could not open the invoice.";
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try { message = JSON.parse(await blob.text()).detail ?? message; } catch { /* keep default */ }
      }
      setError(message);
    } finally {
      setOpening(null);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closePreview(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const selectedPartner = partners.find((p) => p.id === draft?.partner_email);
  // An expense whose investor was removed from Partners still shows its stored name.
  const partnerOptions = editRow && !partners.some((p) => p.id === editRow.email.toLowerCase())
    ? [...partners, { id: editRow.email.toLowerCase(), name: editRow.investor, email: editRow.email, nominee: "" }]
    : partners;

  const q = filter.trim().toLowerCase();
  const visible = (rows ?? []).filter((r) => !q || `${r.investor} ${r.email} ${r.date}`.toLowerCase().includes(q));
  const total = visible.reduce((sum, r) => sum + (r.amount ?? 0), 0);

  // Per-investor totals for one calendar year (current year by default).
  const years = Array.from(new Set([String(new Date().getFullYear()), ...(rows ?? []).map((r) => r.date.slice(0, 4)).filter(Boolean)]))
    .sort().reverse();
  const byInvestor = new Map<string, { investor: string; email: string; count: number; total: number }>();
  for (const r of rows ?? []) {
    if (r.date.slice(0, 4) !== year) continue;
    const key = r.email.toLowerCase();
    const entry = byInvestor.get(key) ?? { investor: r.investor, email: r.email, count: 0, total: 0 };
    entry.count += 1;
    entry.total += r.amount ?? 0;
    byInvestor.set(key, entry);
  }
  const summary = [...byInvestor.values()].sort((a, b) => b.total - a.total);
  const yearTotal = summary.reduce((sum, s) => sum + s.total, 0);
  const yearCount = summary.reduce((sum, s) => sum + s.count, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {preview && (
        <div style={S.overlay} onClick={closePreview} role="dialog" aria-modal="true" aria-label="Invoice preview">
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{preview.row.investor} · {preview.row.date} · {money(preview.row.amount)}</div>
                <div style={{ ...S.sub, whiteSpace: "nowrap" }} title={preview.row.invoice_path}>{preview.row.invoice_path || preview.row.invoice_name}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <a style={S.secondaryBtn} href={preview.url} download={preview.row.invoice_name || "invoice"}>Download</a>
                <a style={S.secondaryBtn} href={preview.url} target="_blank" rel="noreferrer">New tab</a>
                <button style={S.secondaryBtn} onClick={closePreview}>Close</button>
              </div>
            </div>
            <div style={S.modalBody}>
              {preview.type.startsWith("image/") && preview.type !== "image/heic" ? (
                <img src={preview.url} alt={preview.row.invoice_name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              ) : preview.type === "application/pdf" ? (
                <iframe src={preview.url} title={preview.row.invoice_name} style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} />
              ) : (
                <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>
                  This file type can't be previewed in the browser. Use Download to open it.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {draft && (
        <div style={S.card}>
          <h3 style={{ ...S.title, marginBottom: 16 }}>{editRow ? "Edit expense" : "Add expense"}</h3>
          <div style={S.form}>
            <label style={S.field}>
              <span style={S.label}>Investor</span>
              <select style={S.input} value={draft.partner_email} disabled={!isAdmin}
                onChange={(e) => setDraft({ ...draft, partner_email: e.target.value })}>
                <option value="">Choose an investor</option>
                {partnerOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {selectedPartner && <span style={S.sub}>{selectedPartner.email}</span>}
            </label>
            <label style={S.field}>
              <span style={S.label}>Expense amount ($)</span>
              <input style={S.input} type="number" min="0.01" step="0.01" inputMode="decimal"
                value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </label>
            <label style={S.field}>
              <span style={S.label}>Date</span>
              <input style={S.input} type="date" max={today()}
                value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </label>
            <label style={S.field}>
              <span style={S.label}>{editRow?.has_invoice ? "Replace invoice (optional)" : "Invoice, image or PDF (optional)"}</span>
              <input ref={fileInput} style={{ ...S.input, padding: "7px 10px" }} type="file" accept={ACCEPT}
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
              {editRow && !file && editRow.invoice_name && <span style={S.sub}>Current: {editRow.invoice_path || editRow.invoice_name}</span>}
            </label>
            <div style={S.formActions}>
              <button style={{ ...S.primaryBtn, ...(busy ? S.disabled : {}) }} disabled={busy} onClick={save}>
                {busy ? (file ? "Uploading…" : "Saving…") : editRow ? "Save changes" : "Add expense"}
              </button>
              <button style={S.secondaryBtn} disabled={busy} onClick={closeForm}>Cancel</button>
            </div>
            <p style={S.hint}>
              Invoices are saved in Google Drive under invoice/&lt;year&gt;, named with the investor and the upload date and time (UTC),
              for example "invoice/{(draft.date || today()).slice(0, 4)}/{(selectedPartner?.name || "Investor Name").trim().replace(/\s+/g, "_")}_20261018_165725_receipt.pdf". Up to {MAX_MB} MB.
            </p>
          </div>
        </div>
      )}

      {rows !== null && (
        <div style={S.card}>
          <div style={S.header}>
            <h3 style={S.title}>{isAdmin ? `Expenses by investor in ${year}` : `Your expenses in ${year}`}</h3>
            <select style={{ ...S.search, minWidth: 0 }} value={year} onChange={(e) => setYear(e.target.value)} aria-label="Year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ ...S.table, minWidth: 480 }}>
              <thead>
                <tr>{["Investor", "Expenses", "Total"].map((h) => (
                  <th key={h} style={{ ...S.th, ...(h !== "Investor" ? { textAlign: "right" } : {}) }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.email}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 500 }}>{s.investor}</div>
                      <div style={S.sub}>{s.email}</div>
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>{s.count}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{money(s.total)}</td>
                  </tr>
                ))}
                {summary.length === 0 ? (
                  <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={3}>No expenses in {year}.</td></tr>
                ) : (
                  <tr>
                    <td style={{ ...S.td, fontWeight: 600 }}>{isAdmin ? "All investors" : "Total"}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{yearCount}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{money(yearTotal)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={S.header}>
          <h3 style={S.title}>Expenses {rows && <span style={S.count}>({visible.length} · {money(total)})</span>}</h3>
          <div style={S.headerActions}>
            <input style={S.search} placeholder={isAdmin ? "Filter by investor, email or date" : "Filter by date"}
              value={filter} onChange={(e) => setFilter(e.target.value)} />
            {!draft && (isAdmin || me) && <button style={S.primaryBtn} onClick={() => openForm()}>Add expense</button>}
          </div>
        </div>

        {!isAdmin && me === false && (
          <div style={S.sub}>Your login isn't linked to an investor yet, so you can't add expenses. Ask an admin to add your email in Partners.</div>
        )}

        {notice && <div style={S.success}>{notice}</div>}
        {error && <pre style={S.error}>{error}</pre>}

        {rows === null ? <Spinner /> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={S.table}>
              <thead>
                <tr>{["Date", "Investor", "Amount", "Invoice", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={r.id} style={{
                    ...(i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}),
                    ...(editRow?.id === r.id ? { background: "rgba(29,158,117,0.10)" } : {}),
                  }}>
                    <td style={{ ...S.td, color: "var(--text-secondary)" }}>{r.date}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 500 }}>{r.investor}</div>
                      <div style={S.sub}>{r.email}</div>
                    </td>
                    <td style={S.td}>{money(r.amount)}</td>
                    <td style={{ ...S.td, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.has_invoice ? (
                        <>
                          <button style={S.linkBtn} disabled={opening === r.id} onClick={() => viewInvoice(r)} title={r.invoice_name}>
                            {opening === r.id ? "Opening…" : "View"}
                          </button>
                          {isAdmin && r.invoice_link && (
                            <a style={{ ...S.linkBtn, marginLeft: 10 }} href={r.invoice_link} target="_blank" rel="noreferrer">Drive</a>
                          )}
                          <button style={{ ...S.sub, ...S.pathBtn }} disabled={opening === r.id} onClick={() => viewInvoice(r)}
                            title={`View ${r.invoice_path || r.invoice_name}`}>
                            {r.invoice_path || r.invoice_name}
                          </button>
                        </>
                      ) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button style={S.rowBtn} disabled={busy} onClick={() => openForm(r)}>Edit</button>
                      <button style={{ ...S.rowBtn, ...S.deleteBtn }} disabled={busy} onClick={() => remove(r)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {rows.length > 0 && visible.length === 0 && (
                  <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No expenses match.</td></tr>
                )}
                {rows.length === 0 && (
                  <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No expenses yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  form: {
    display: "grid", gap: 16,
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%", boxSizing: "border-box",
  },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)", textDecoration: "none",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  sub: { fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis" },
  pathBtn: { display: "block", padding: 0, textAlign: "left", maxWidth: "100%", cursor: "pointer", textDecoration: "underline dotted" },
  overlay: {
    position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  modal: {
    background: "var(--dark-bg, #0b0f0e)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12,
    width: "min(960px, 100%)", height: "min(90vh, 1100px)", display: "flex", flexDirection: "column", overflow: "hidden",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
    padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", color: "var(--text-primary)", fontSize: 14,
  },
  modalBody: { flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 8, overflow: "auto" },
  linkBtn: { color: "var(--teal-light)", fontSize: 13, fontWeight: 500, padding: 0, textDecoration: "none" },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
};
